/* eslint-disable */
import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import {
  Col,
  Form,
  Input,
  Row,
  Tag,
  Typography
} from 'antd'
import { fetchSystemPermissionsList } from '@/redux/use/systemPermissionSlice'
import {
  buildPermissionAntTreeData,
  getRolePermissionCheckedKeysWithAncestors
} from '@/utils/rolePermissionTree'
import { PermissionTreeField } from './RolePermissionTreeField'

const { TextArea } = Input
const { Text } = Typography
const { CheckableTag } = Tag

const DataScopeTagSelect = ({ value, onChange, options, loading }) => {
  if (loading) {
    return <span style={{ color: '#999' }}>资源范围加载中...</span>
  }
  if (!options.length) {
    return <span style={{ color: '#999' }}>暂无资源范围</span>
  }

  return (
    <div className="i2-radio-tag">
      {options.map((item) => (
        <CheckableTag
          key={item.id}
          checked={String(value) === String(item.id)}
          onChange={(checked) => {
            if (checked) onChange(item.id)
          }}
        >
          {item.value}
        </CheckableTag>
      ))}
    </div>
  )
}

DataScopeTagSelect.defaultProps = {
  options: [],
  loading: false
}

DataScopeTagSelect.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  options: PropTypes.array,
  loading: PropTypes.bool
}

const mapStateToProps = (state) => ({
  permissionsList: state.use.systemPermission.list,
  permissionsFetching: state.use.systemPermission.fetching,
  enumOptions: state.components.enumOptions
})

const mapDispatchToProps = {
  fetchSystemPermissionsList
}

/**
 * 新建角色表单（由 WrappedDrawer 注入 form ref）
 * 大屏：左栏基础信息、右栏权限树，利用横向空间；小屏：纵向堆叠、抽屉全宽
 */
class RoleCreateForm extends React.Component {
  static propTypes = {
    form: PropTypes.object,
    permissionsList: PropTypes.array,
    permissionsFetching: PropTypes.bool,
    fetchSystemPermissionsList: PropTypes.func,
    enumOptions: PropTypes.object,
    /** create | edit；编辑时不展示权限树、不提交 permissionIds */
    actType: PropTypes.oneOf(['create', 'edit']),
    /** 编辑时传入角色行，用于回填表单 */
    initialRecord: PropTypes.object
  }

  static defaultProps = {
    actType: 'create'
  }

  componentDidMount() {
    const {
      actType,
      permissionsList,
      fetchSystemPermissionsList: loadPerm,
      initialRecord
    } = this.props

    const afterPerm = () => {
      if (initialRecord) {
        this.applyInitialRecord()
      }
    }
    if (actType === 'edit') {
      afterPerm()
      return
    }
    if (!permissionsList || permissionsList.length === 0) {
      loadPerm().finally(afterPerm)
    } else {
      afterPerm()
    }
  }

  applyInitialRecord = () => {
    const { initialRecord, form, actType, permissionsList } = this.props
    if (!initialRecord || !form?.current) return
    let dataScopeRaw = initialRecord.dataScope
    if (dataScopeRaw != null && typeof dataScopeRaw === 'object' && 'value' in dataScopeRaw) {
      dataScopeRaw = dataScopeRaw.value
    }
    const dataScopeVal =
      dataScopeRaw != null && dataScopeRaw !== '' ? String(dataScopeRaw) : undefined
    const values = {
      name: initialRecord.name,
      code: initialRecord.code,
      remark: initialRecord.remark ?? '',
      dataScope: dataScopeVal
    }
    if (actType !== 'edit') {
      const fullTree = buildPermissionAntTreeData(permissionsList || [])
      values.permissionIds = getRolePermissionCheckedKeysWithAncestors(initialRecord, fullTree)
    }
    form.current.setFieldsValue(values)
  }

  getFullTreeData = () => {
    const { permissionsList } = this.props
    return buildPermissionAntTreeData(permissionsList || [])
  }

  render() {
    const {
      form,
      permissionsFetching,
      actType,
      enumOptions: {
        DataScopeType: { fetching: dsEnumFetching = false, data: dsEnumData = [] } = {}
      } = {}
    } = this.props
    const isEdit = actType === 'edit'
    const fullTree = this.getFullTreeData()
    const dataScopeTagOptions = dsEnumData.map((o) => ({
      id: o.id,
      value: o.value
    }))

    return (
      <Form
        ref={form}
        layout="vertical"
        initialValues={isEdit ? {} : { permissionIds: [] }}
        style={{ width: '100%', maxWidth: '100%' }}
      >
        <Row gutter={[16, 16]} wrap>
          <Col xs={24} lg={isEdit ? 24 : 9} xl={isEdit ? 24 : 8}>
            <Form.Item
              label="角色名称"
              name="name"
              rules={[{ required: true, message: '请输入角色名称' }]}
            >
              <Input placeholder="请输入" allowClear maxLength={64} />
            </Form.Item>
            <Form.Item
              label="角色编码"
              name="code"
              rules={[{ required: true, message: '请输入角色编码' }]}
            >
              <Input placeholder="英文、数字、下划线等" allowClear maxLength={64} />
            </Form.Item>
            <Form.Item
              label="资源范围"
              name="dataScope"
              rules={[{ required: true, message: '请选择资源范围' }]}
              tooltip="控制该角色可操作的数据范围，选项来自枚举接口"
            >
              <DataScopeTagSelect
                loading={dsEnumFetching}
                options={dataScopeTagOptions}
              />
            </Form.Item>
            <Form.Item label="备注" name="remark">
              <TextArea rows={3} placeholder="选填" allowClear maxLength={500} showCount />
            </Form.Item>
          </Col>
          {!isEdit ? (
            <Col xs={24} lg={15} xl={16}>
              <Form.Item
                label="角色权限"
                name="permissionIds"
                tooltip="数据来自全量权限接口，可搜索、全选/清空。"
                extra={
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    仅最末级可勾选；分类名前的灯泡表示其下已有选中项。
                  </Text>
                }
                style={{ marginBottom: 0 }}
              >
                <PermissionTreeField fullTreeData={fullTree} loading={permissionsFetching} />
              </Form.Item>
            </Col>
          ) : null}
        </Row>
      </Form>
    )
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(RoleCreateForm)
